import React, { useEffect } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { useSnapshotsStore } from "@/store/snapshots";
import { useTariffLayer } from "@/store/mode";
import { useTariffSelectorUiStore } from "@/store/tariffSelectorUi";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";
import {
  SNAPSHOT_DAY_LABELS,
  SNAPSHOT_SLOT_DEFINITIONS,
  SnapshotDay,
} from "@/types/snapshots";
import cls from "./styles.module.scss";

const DAYS: SnapshotDay[] = ["yesterday", "today"];
const PILL_GAP_PX = 0;

const getPillLeft = (index: number, itemsCount: number) =>
  `calc(${index} * ((100% - ${
    (itemsCount - 1) * PILL_GAP_PX
  }px) / ${itemsCount} + ${PILL_GAP_PX}px))`;

export const SnapshotSelector = () => {
  const tariffLayer = useTariffLayer((s) => s.tariffLayer);
  const disabled = useTariffSelectorUiStore((s) => s.isServiceMenuOpen);
  const {
    selectedDay,
    selectedSlot,
    timestampsByDay,
    isLoading,
    initialize,
    setSelectedDay,
    setSelectedSlot,
  } = useSnapshotsStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const dayTimestamps = timestampsByDay[selectedDay];
  const { useSnapshots } = getTariffModeConfig(tariffLayer);

  if (!useSnapshots) return null;

  return (
    <div
      className={clsx(cls.root, disabled && cls.modeMenuOpen)}
      aria-disabled={disabled}
    >
      <div className={cls.days}>
        <motion.div
          className={cls.pill}
          animate={{
            left: getPillLeft(DAYS.indexOf(selectedDay), DAYS.length),
          }}
          transition={{ type: "spring", stiffness: 380, damping: 34 }}
        />
        {DAYS.map((day) => {
          const active = day === selectedDay;

          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              className={clsx(cls.dayButton, active && cls.active)}
              onClick={() => setSelectedDay(day)}
            >
              {SNAPSHOT_DAY_LABELS[day]}
            </button>
          );
        })}
      </div>

      <div className={cls.slots}>
        <motion.div
          className={cls.pill}
          animate={{
            left: getPillLeft(
              SNAPSHOT_SLOT_DEFINITIONS.findIndex(
                ({ key }) => key === selectedSlot
              ),
              SNAPSHOT_SLOT_DEFINITIONS.length
            ),
          }}
          transition={{ type: "spring", stiffness: 380, damping: 34 }}
        />
        {SNAPSHOT_SLOT_DEFINITIONS.map(({ key, label }) => {
          const isAvailable = Boolean(dayTimestamps?.[key]?.snapshot_timestamp);
          const active = key === selectedSlot && isAvailable;

          return (
            <button
              key={key}
              type="button"
              disabled={disabled || !isAvailable || isLoading}
              className={clsx(
                cls.slotButton,
                active && cls.active,
                !isAvailable && cls.disabled
              )}
              onClick={() => setSelectedSlot(key)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
